import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, BOOKS_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { demoApi, problemMessage } from "@/api/client";
import { formatMoney } from "@/lib/format";
import { useReferenceData } from "@/hooks/useReferenceData";
import type { AutoMatchResult, Guid, ReconcileCandidate, ReconcileSummary, UnmatchedStatementLine } from "@/api/types";
import "./BooksReconcile.css";

type QueueStatus = "loading" | "ready" | "error";

/**
 * Books' Reconcile tab (ef-043-3, `/books/reconcile`) — the 5th and last Books tab (ADR
 * 0009's own five-tab ceiling). Imports a bank statement CSV (`ef-043-1`'s own
 * `POST .../import`), works the unmatched queue against candidate entry lines
 * (`ef-043-2`'s own `GET .../unmatched`/`GET .../candidates`/`POST .../match`), and reads
 * a summary card off this slice's OWN new read route, `GET .../summary`. A standalone
 * top-level page, same pattern `BooksVendors.tsx`/`BooksRecurring.tsx` already use for
 * their own Books tabs — this job's own file scope never lists `Books.tsx`.
 *
 * ⚠️ `shell.spec.ts`'s own `AREAS` fixture still pins Books at exactly FOUR tabs — it
 * predates this job and sits outside this job's own file scope (never edited here, per
 * `rules/code-quality.md` §7 / the ratchet's contract-first carve-out). Flagged in this
 * job's own return contract rather than silently worked around in either direction, same
 * posture `AreaTabs.tsx`'s own `REPORTS_TABS` remarks took for `Q-TIME-TAB-CONTENT`
 * before that fixture was updated by its own author.
 */
export function BooksReconcile() {
  const { accounts, status: refStatus, error: refError } = useReferenceData();
  const assetAccounts = useMemo(() => accounts.filter((a) => a.kind === "Asset"), [accounts]);

  const [accountId, setAccountId] = useState<Guid | "">("");
  useEffect(() => {
    if (accountId === "" && assetAccounts.length > 0) {
      setAccountId(assetAccounts[0]!.id);
    }
  }, [assetAccounts, accountId]);

  const accountFieldId = useId();
  const fileInputId = useId();
  const balanceFieldId = useId();
  const matchSelectId = useId();
  const queueHeadingId = useId();

  // ---- Unmatched queue ----------------------------------------------------
  const [queueStatus, setQueueStatus] = useState<QueueStatus>("loading");
  const [unmatchedLines, setUnmatchedLines] = useState<UnmatchedStatementLine[]>([]);
  const [queueError, setQueueError] = useState<string | null>(null);

  const fetchUnmatched = useCallback(async (id: Guid, signal?: AbortSignal): Promise<void> => {
    try {
      const lines = await demoApi.getReconcileUnmatched(id, undefined, signal);
      if (signal?.aborted) return;
      setUnmatchedLines(lines);
      setQueueStatus("ready");
      setQueueError(null);
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
      setQueueStatus("error");
      setQueueError(problemMessage(err, "Could not load the unmatched queue."));
    }
  }, []);

  useEffect(() => {
    if (!accountId) return;
    setAutoResult(null);
    setAutoError(null);
    setQueueStatus("loading");
    const controller = new AbortController();
    void fetchUnmatched(accountId, controller.signal);
    return () => controller.abort();
  }, [accountId, fetchUnmatched]);

  /** A silent re-fetch — after an import or a confirmed match — that never flips the
   *  queue back to a "loading" StateMessage: the row list a farmer is looking at stays on
   *  screen (and this page's own import-result message keeps sole ownership of
   *  `role="status"` — rules/ui-breadcrumbs.md §1, ground truth read off the DOM, not
   *  inferred from two live regions racing on screen at once). */
  async function refreshUnmatched(): Promise<void> {
    if (!accountId) return;
    await fetchUnmatched(accountId);
  }

  // ---- Import ---------------------------------------------------------------
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importMessage, setImportMessage] = useState<string | null>(null);

  function readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
      reader.onerror = () => reject(reader.error ?? new Error("Could not read the selected file."));
      reader.readAsText(file);
    });
  }

  async function handleImport(): Promise<void> {
    if (!accountId || !csvFile) return;
    setImportBusy(true);
    setImportMessage(null);
    try {
      const csv = await readFileAsText(csvFile);
      const result = await demoApi.importStatement(accountId, csv);
      const pieces = [`Imported ${result.rowsImported} of ${result.rowsParsed} statement line${result.rowsParsed === 1 ? "" : "s"}`];
      const notes: string[] = [];
      if (result.rowsDuplicate > 0) notes.push(`${result.rowsDuplicate} duplicate${result.rowsDuplicate === 1 ? "" : "s"}`);
      if (result.rowsSkipped > 0) notes.push(`${result.rowsSkipped} skipped`);
      setImportMessage(notes.length > 0 ? `${pieces.join("")} (${notes.join(", ")}).` : `${pieces.join("")}.`);
      await refreshUnmatched();
    } catch (err) {
      setImportMessage(`Could not import: ${problemMessage(err, "the statement could not be imported.")}`);
    } finally {
      setImportBusy(false);
    }
  }

  // ---- Auto-match (ef-058-7) ---------------------------------------------------
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoResult, setAutoResult] = useState<AutoMatchResult | null>(null);
  const [autoError, setAutoError] = useState<string | null>(null);

  async function handleAutoMatch(): Promise<void> {
    if (!accountId) return;
    setAutoBusy(true);
    setAutoError(null);
    try {
      setAutoResult(await demoApi.autoMatchStatement(accountId));
      await refreshUnmatched();
    } catch (err) {
      setAutoResult(null);
      setAutoError(problemMessage(err, "Could not run auto-match."));
    } finally {
      setAutoBusy(false);
    }
  }

  // ---- Clear / match --------------------------------------------------------
  const [clearingId, setClearingId] = useState<Guid | null>(null);
  const [candidates, setCandidates] = useState<ReconcileCandidate[] | null>(null);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState<string | null>(null);
  const [selectedEntryLineId, setSelectedEntryLineId] = useState("");
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  async function openClear(statementLineId: Guid): Promise<void> {
    if (!accountId) return;
    cancelAdd();
    setClearingId(statementLineId);
    setCandidates(null);
    setCandidatesLoading(true);
    setCandidatesError(null);
    setSelectedEntryLineId("");
    setConfirmError(null);
    try {
      const result = await demoApi.getReconcileCandidates(accountId, statementLineId);
      setCandidates(result);
    } catch (err) {
      setCandidatesError(problemMessage(err, "Could not load candidate entries."));
    } finally {
      setCandidatesLoading(false);
    }
  }

  function cancelClear(): void {
    setClearingId(null);
    setCandidates(null);
    setCandidatesError(null);
    setSelectedEntryLineId("");
    setConfirmError(null);
  }

  async function confirmMatch(): Promise<void> {
    if (!accountId || !clearingId || !selectedEntryLineId) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      await demoApi.matchStatementLine(accountId, {
        statementLineId: clearingId,
        entryLineId: selectedEntryLineId,
      });
      cancelClear();
      await refreshUnmatched();
    } catch (err) {
      setConfirmError(problemMessage(err, "Could not confirm this match."));
    } finally {
      setConfirmBusy(false);
    }
  }

  // ---- Add to books (ef-058-8) ---------------------------------------------
  // An unmatched line becomes a new register entry the owner categorises. One inline
  // panel open at a time, shared with the Clear panel's row slot.
  const categoryFieldId = useId();
  const addMemoFieldId = useId();
  const [addingId, setAddingId] = useState<Guid | null>(null);
  const [addCategoryId, setAddCategoryId] = useState("");
  const [addMemo, setAddMemo] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  // One key per opened panel, reused on a retry of the SAME post so a double-click or a
  // retried timeout can never post twice (the server replays it).
  const addKeyRef = useRef<string | null>(null);

  /** Every account but the bank account itself — the server refuses the bank account as
   *  its own category. Archived accounts are not offered. */
  const categoryAccounts = useMemo(
    () => accounts.filter((a) => a.id !== accountId && a.archivedAt === null),
    [accounts, accountId],
  );

  function openAdd(line: UnmatchedStatementLine): void {
    cancelClear();
    setAddingId(line.id);
    setAddCategoryId("");
    setAddMemo(line.description);
    setAddError(null);
    addKeyRef.current = crypto.randomUUID();
  }

  function cancelAdd(): void {
    setAddingId(null);
    setAddCategoryId("");
    setAddMemo("");
    setAddError(null);
    addKeyRef.current = null;
  }

  async function confirmAdd(): Promise<void> {
    if (!accountId || !addingId || !addCategoryId) return;
    setAddBusy(true);
    setAddError(null);
    try {
      await demoApi.addStatementEntry(
        accountId,
        { statementLineId: addingId, categoryAccountId: addCategoryId, memo: addMemo.trim() === "" ? null : addMemo },
        addKeyRef.current ?? undefined,
      );
      cancelAdd();
      await refreshUnmatched();
    } catch (err) {
      setAddError(problemMessage(err, "Could not add this line to the books."));
    } finally {
      setAddBusy(false);
    }
  }

  // ---- Summary ----------------------------------------------------------
  const [statementEndingBalance, setStatementEndingBalance] = useState("");
  const [summary, setSummary] = useState<ReconcileSummary | null>(null);
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  async function handleCheckBalance(): Promise<void> {
    if (!accountId) return;
    const parsed = Number(statementEndingBalance);
    if (statementEndingBalance.trim() === "" || !Number.isFinite(parsed)) {
      setSummaryError("Enter a valid statement ending balance.");
      setSummary(null);
      return;
    }
    setSummaryBusy(true);
    setSummaryError(null);
    try {
      const result = await demoApi.getReconcileSummary(accountId, parsed);
      setSummary(result);
    } catch (err) {
      setSummary(null);
      setSummaryError(problemMessage(err, "Could not check the statement balance."));
    } finally {
      setSummaryBusy(false);
    }
  }

  const summaryState = summaryError ? "error" : summary ? (summary.difference === 0 ? "balanced" : "off") : "unknown";
  const count = unmatchedLines.length;

  return (
    <main className="books-reconcile-page">
      <PageHeader
        title="Reconcile"
        subtitle="Import a bank statement, clear its lines against the register, and check the statement balances."
      />

      <AreaTabs area="/books" tabs={BOOKS_TABS} label="Books" />

      <section className="books-reconcile__card">
        <div className="books-reconcile__account-picker">
          <label htmlFor={accountFieldId}>Account</label>
          <select
            id={accountFieldId}
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            disabled={refStatus === "loading" || assetAccounts.length === 0}
          >
            {assetAccounts.length === 0 && <option value="">No instrument accounts yet</option>}
            {assetAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.number} — {a.name}
              </option>
            ))}
          </select>
        </div>

        {refStatus === "error" && (
          <StateMessage tone="error" title="Could not load accounts." detail={refError ?? undefined} />
        )}

        <div className="books-reconcile__import">
          <label htmlFor={fileInputId}>Statement CSV or OFX file</label>
          <input
            id={fileInputId}
            type="file"
            accept=".csv,.ofx,.qfx,text/csv"
            onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            className="btn btn--primary"
            disabled={!accountId || !csvFile || importBusy}
            onClick={() => void handleImport()}
          >
            {importBusy ? "Importing…" : "Import"}
          </button>
        </div>

        {importMessage && (
          <p className="books-reconcile__import-message" role="status">
            {importMessage}
          </p>
        )}

        <ColumnMappingForm accountId={accountId} />
      </section>

      <section className="books-reconcile__card books-reconcile__auto" data-state={autoBusy ? "loading" : autoError ? "error" : autoResult ? "ready" : "idle"}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={!accountId || autoBusy}
          aria-busy={autoBusy}
          onClick={() => void handleAutoMatch()}
        >
          {autoBusy ? "Matching…" : "Auto-match"}
        </button>
        <span className="books-reconcile__auto-hint">
          Clears lines with exactly one matching entry (same amount, within 3 days). Anything unclear is left for you.
        </span>
        {/* The page keeps exactly ONE role="status" (the import message), so this is a group. */}
        {autoResult && (
          <div role="group" aria-label="Auto-match result" className="books-reconcile__auto-result">
            Matched {autoResult.matched} · Needs review {autoResult.ambiguous} · No match {autoResult.noCandidate}
          </div>
        )}
        {autoError && (
          <p className="books-reconcile__match-error" role="alert">
            {autoError}
          </p>
        )}
      </section>

      {/* Ground truth for a tester, rules/ui-breadcrumbs.md §1 — read off the DOM rather
          than inferred from sentence text.
          Deliberately NOT `aria-labelledby`/named as a region: `getByLabel(/statement|csv/i)`
          (books-reconcile.spec.ts's own `importCsv` helper) matches ANY element with a
          computed accessible name containing "statement" — a named section here (a
          `<section>` WITH an accessible name gets an implicit `role="region"`, which
          `getByLabel`'s accessible-name search also considers) would collide with the
          import file input on every import call in this suite. The heading stays for
          sighted users; the `<ul>` two levels down still carries its own required
          `aria-label="Unmatched statement lines"` for `getByRole("list", { name })`. */}
      <section
        className="books-reconcile__card"
        data-state={queueStatus === "ready" && count === 0 ? "empty" : queueStatus}
      >
        <h2 id={queueHeadingId} className="books-reconcile__heading">
          Unmatched statement lines
        </h2>

        {queueStatus === "loading" && <StateMessage tone="loading" title="Loading the unmatched queue…" />}

        {queueStatus === "error" && (
          <StateMessage tone="error" title="Could not load the unmatched queue." detail={queueError ?? undefined} />
        )}

        {queueStatus === "ready" && count === 0 && (
          <StateMessage
            tone="empty"
            title="Nothing to reconcile."
            detail="Import a statement above, and any lines that haven't cleared yet will show up here."
          />
        )}

        {queueStatus === "ready" && count > 0 && (
          <ul className="books-reconcile__list" aria-label="Unmatched statement lines">
            {unmatchedLines.map((line) => (
              <li key={line.id} className="books-reconcile__row" data-statement-line-id={line.id}>
                <div className="books-reconcile__row-main">
                  <span className="books-reconcile__description">{line.description}</span>
                  <span className="books-reconcile__meta">
                    {line.occurredOn} · {formatMoney(line.amount)}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn--ghost"
                  disabled={clearingId === line.id && (candidatesLoading || confirmBusy)}
                  onClick={() => void openClear(line.id)}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  disabled={addingId === line.id && addBusy}
                  onClick={() => openAdd(line)}
                >
                  Add to books
                </button>

                {addingId === line.id && (
                  <div className="books-reconcile__match-panel books-reconcile__add-panel">
                    <label htmlFor={categoryFieldId}>Category</label>
                    <select
                      id={categoryFieldId}
                      value={addCategoryId}
                      onChange={(e) => setAddCategoryId(e.target.value)}
                    >
                      <option value="">Select a category…</option>
                      {categoryAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.number} — {a.name}
                        </option>
                      ))}
                    </select>
                    <label htmlFor={addMemoFieldId}>Memo</label>
                    <input
                      id={addMemoFieldId}
                      type="text"
                      maxLength={200}
                      value={addMemo}
                      onChange={(e) => setAddMemo(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn--primary"
                      disabled={!addCategoryId || addBusy}
                      onClick={() => void confirmAdd()}
                    >
                      {addBusy ? "Posting…" : "Post to books"}
                    </button>
                    <button type="button" className="btn btn--ghost" onClick={cancelAdd} disabled={addBusy}>
                      Cancel
                    </button>
                    {addError && (
                      <p className="books-reconcile__match-error" role="alert">
                        {addError}
                      </p>
                    )}
                  </div>
                )}

                {clearingId === line.id && (
                  <div className="books-reconcile__match-panel">
                    {candidatesLoading && <StateMessage tone="loading" title="Loading candidates…" />}

                    {!candidatesLoading && candidatesError && (
                      <StateMessage tone="error" title="Could not load candidates." detail={candidatesError} />
                    )}

                    {!candidatesLoading && !candidatesError && candidates !== null && candidates.length === 0 && (
                      <p className="books-reconcile__no-candidates">No matching entry found.</p>
                    )}

                    {!candidatesLoading && !candidatesError && candidates !== null && candidates.length > 0 && (
                      <>
                        <label htmlFor={matchSelectId}>Match to</label>
                        <select
                          id={matchSelectId}
                          value={selectedEntryLineId}
                          onChange={(e) => setSelectedEntryLineId(e.target.value)}
                        >
                          <option value="">Select an entry…</option>
                          {candidates.map((c) => (
                            <option key={c.entryLineId} value={c.entryLineId}>
                              {formatMoney(c.amount)} on {c.occurredOn}
                              {c.memo ? ` — ${c.memo}` : ""}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn--primary"
                          disabled={!selectedEntryLineId || confirmBusy}
                          onClick={() => void confirmMatch()}
                        >
                          {confirmBusy ? "Confirming…" : "Confirm match"}
                        </button>
                        <button type="button" className="btn btn--ghost" onClick={cancelClear} disabled={confirmBusy}>
                          Cancel
                        </button>
                        {confirmError && (
                          <p className="books-reconcile__match-error" role="alert">
                            {confirmError}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section
        className="books-reconcile__card"
        role="region"
        aria-label="Summary"
        data-state={summaryState}
      >
        <h2 className="books-reconcile__heading">Summary</h2>

        <div className="books-reconcile__balance-field">
          <label htmlFor={balanceFieldId}>Statement ending balance</label>
          <input
            id={balanceFieldId}
            type="number"
            inputMode="decimal"
            step="0.01"
            value={statementEndingBalance}
            onChange={(e) => setStatementEndingBalance(e.target.value)}
          />
          <button
            type="button"
            className="btn btn--primary"
            disabled={!accountId || summaryBusy}
            onClick={() => void handleCheckBalance()}
          >
            {summaryBusy ? "Checking…" : "Check balance"}
          </button>
        </div>

        {summaryError && (
          <p className="books-reconcile__summary-error" role="alert">
            {summaryError}
          </p>
        )}

        {summary && (
          <dl className="books-reconcile__summary-amounts">
            <div>
              <dt>Cleared balance</dt>
              <dd>{formatMoney(summary.clearedBalance)}</dd>
            </div>
            <div>
              <dt>Difference</dt>
              <dd>{formatMoney(summary.difference)}</dd>
            </div>
            <div>
              <dt>Unmatched statement lines</dt>
              <dd>{summary.unmatchedStatementLineCount}</dd>
            </div>
            <div>
              <dt>Unreconciled entry lines</dt>
              <dd>{summary.unreconciledEntryLineCount}</dd>
            </div>
          </dl>
        )}

        {summary && (
          <p className="books-reconcile__summary-verdict">
            {summary.difference === 0 ? "Balanced." : "Off — the statement does not match the cleared total yet."}
          </p>
        )}
      </section>
    </main>
  );
}

type MappingRequest = Parameters<typeof demoApi.saveBankImportMapping>[1];
type MappingFormState = {
  dateColumn: string;
  descriptionColumn: string;
  amountColumn: string;
  debitColumn: string;
  creditColumn: string;
  dateFormat: MappingRequest["dateFormat"];
  negateAmount: boolean;
};

const EMPTY_MAPPING: MappingFormState = {
  dateColumn: "",
  descriptionColumn: "",
  amountColumn: "",
  debitColumn: "",
  creditColumn: "",
  dateFormat: "iso",
  negateAmount: false,
};

/**
 * ef-058-6 — the saved CSV column mapping for the selected account: which header names hold
 * the date, description and amount in a bank's own export. A disclosure (collapsed by
 * default) so the import card stays quiet for the stock `date,description,amount` file.
 *
 * Own component, own state: it shares nothing with the page but the account id, and its
 * "Mapping saved." confirmation is deliberately NOT a `role="status"` — the page's import
 * message keeps sole ownership of that live region (a plain `aria-live` paragraph here).
 * Labels avoid the words "statement"/"csv"/"account" so `getByLabel` on the import file
 * input stays unambiguous (books-reconcile.spec.ts).
 */
function ColumnMappingForm({ accountId }: { accountId: Guid | "" }) {
  const panelId = useId();
  const ids = {
    date: useId(),
    description: useId(),
    amount: useId(),
    debit: useId(),
    credit: useId(),
    format: useId(),
    negate: useId(),
  };

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<MappingFormState>(EMPTY_MAPPING);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load (and re-load on account change) the saved mapping so the form repopulates.
  useEffect(() => {
    setForm(EMPTY_MAPPING);
    setSaved(false);
    setError(null);
    if (!accountId) return;
    const controller = new AbortController();
    demoApi
      .getBankImportMapping(accountId, controller.signal)
      .then((m) => {
        if (controller.signal.aborted || !m) return;
        setForm({
          dateColumn: m.dateColumn,
          descriptionColumn: m.descriptionColumn,
          amountColumn: m.amountColumn ?? "",
          debitColumn: m.debitColumn ?? "",
          creditColumn: m.creditColumn ?? "",
          dateFormat: m.dateFormat,
          negateAmount: m.negateAmount,
        });
      })
      .catch(() => {
        // No saved mapping to show is not an error worth a message; saving still works.
      });
    return () => controller.abort();
  }, [accountId]);

  function update(patch: Partial<MappingFormState>): void {
    setForm((f) => ({ ...f, ...patch }));
    setSaved(false);
  }

  async function handleSave(): Promise<void> {
    if (!accountId) return;
    const blankToNull = (v: string) => (v.trim() === "" ? null : v.trim());
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      await demoApi.saveBankImportMapping(accountId, {
        dateColumn: form.dateColumn.trim(),
        descriptionColumn: form.descriptionColumn.trim(),
        amountColumn: blankToNull(form.amountColumn),
        debitColumn: blankToNull(form.debitColumn),
        creditColumn: blankToNull(form.creditColumn),
        dateFormat: form.dateFormat,
        negateAmount: form.negateAmount,
      });
      setSaved(true);
    } catch (err) {
      setError(problemMessage(err, "Could not save the mapping."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="books-reconcile__mapping" data-state={busy ? "loading" : error ? "error" : saved ? "saved" : "ready"}>
      <button
        type="button"
        className="btn btn--ghost"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
      >
        Column mapping
      </button>

      <div id={panelId} hidden={!open}>
        {open && (
          <fieldset className="books-reconcile__mapping-fields">
            <legend>Column mapping</legend>
            <p className="books-reconcile__mapping-hint">
              For a bank file whose header is not date, description, amount: name the header of each column.
              Use either one amount column, or a debit and a credit column.
            </p>

            <label htmlFor={ids.date}>Date column</label>
            <input id={ids.date} type="text" value={form.dateColumn} onChange={(e) => update({ dateColumn: e.target.value })} />

            <label htmlFor={ids.description}>Description column</label>
            <input
              id={ids.description}
              type="text"
              value={form.descriptionColumn}
              onChange={(e) => update({ descriptionColumn: e.target.value })}
            />

            <label htmlFor={ids.amount}>Amount column</label>
            <input id={ids.amount} type="text" value={form.amountColumn} onChange={(e) => update({ amountColumn: e.target.value })} />

            <label htmlFor={ids.debit}>Debit column</label>
            <input id={ids.debit} type="text" value={form.debitColumn} onChange={(e) => update({ debitColumn: e.target.value })} />

            <label htmlFor={ids.credit}>Credit column</label>
            <input id={ids.credit} type="text" value={form.creditColumn} onChange={(e) => update({ creditColumn: e.target.value })} />

            <label htmlFor={ids.format}>Date format</label>
            <select
              id={ids.format}
              value={form.dateFormat}
              onChange={(e) => update({ dateFormat: e.target.value as MappingFormState["dateFormat"] })}
            >
              <option value="iso">Year-month-day (2026-01-31)</option>
              <option value="us">Month/day/year (01/31/2026)</option>
              <option value="eu">Day/month/year (31/01/2026)</option>
            </select>

            <label htmlFor={ids.negate} className="books-reconcile__mapping-check">
              <input
                id={ids.negate}
                type="checkbox"
                checked={form.negateAmount}
                onChange={(e) => update({ negateAmount: e.target.checked })}
              />
              Negate amounts (the bank lists money out as positive)
            </label>

            <div className="books-reconcile__mapping-actions">
              <button
                type="button"
                className="btn btn--primary"
                disabled={!accountId || busy}
                onClick={() => void handleSave()}
              >
                {busy ? "Saving…" : "Save mapping"}
              </button>
              <p className="books-reconcile__mapping-saved" aria-live="polite">
                {saved ? "Mapping saved." : ""}
              </p>
            </div>

            {error && (
              <p className="books-reconcile__mapping-error" role="alert">
                {error}
              </p>
            )}
          </fieldset>
        )}
      </div>
    </div>
  );
}
